import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";

export type DiagnosticLevel = "iniciante" | "intermediario" | "avancado";

export interface TrailDiagnostic {
  id: string;
  user_id: string;
  category: string;
  level: DiagnosticLevel;
  score: number;
  created_at: string;
}

export function useTrailDiagnostic(category: string) {
  const user = useUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["trail-diagnostic", user?.id, category],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("user_trail_diagnostic")
        .select("*")
        .eq("user_id", user.id)
        .eq("category", category)
        .maybeSingle();
      return (data as TrailDiagnostic | null) ?? null;
    },
    enabled: !!user?.id && !!category,
  });

  const save = useMutation({
    mutationFn: async ({ level, score }: { level: DiagnosticLevel; score: number }) => {
      if (!user?.id) throw new Error("Not logged in");
      const { error } = await supabase
        .from("user_trail_diagnostic")
        .upsert(
          { user_id: user.id, category, level, score },
          { onConflict: "user_id,category" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trail-diagnostic", user?.id, category] });
    },
  });

  return {
    diagnostic: query.data,
    hasCompleted: !!query.data,
    isLoading: query.isLoading,
    save,
  };
}
